// Talking to the kitchen helper out loud. Matt, 2026-10-02: "I want a set of
// instructions and an out loud conversation with Claude while I'm cooking."
//
// The browser's own speech recognition hears the question and its own
// speech synthesis reads the answer back; nothing new on the server beyond
// a `voice` flag that asks for answers made to be heard. One turn at a time:
// listen, ask, speak, listen again. Recognition is paused while the phone
// speaks, or it would hear itself and answer its own answer.
//
// Safari ends a recognition session at the first pause (and after a few
// seconds of silence), which is exactly the turn-taking wanted here, and why
// the loop simply starts a new one each time. A wake word ("Claude, …")
// would need recognition running all the time, restarting through every
// silence; not built until this loop has been tried on the installed app.

const recognitionClass = () => globalThis.SpeechRecognition || globalThis.webkitSpeechRecognition || null;

export const voiceSupported = () => Boolean(recognitionClass() && globalThis.speechSynthesis);

// The whole utterance has to be a sign-off, so "thanks, how long does it
// rest?" is still a question.
const STOP = /^(?:(?:ok(?:ay)?|alright|all right)\s+)?(?:stop(?: listening| talking)?|thanks?|thank you(?: so much)?|that'?s (?:all|it|everything)|never ?mind|good ?bye|bye|i'?m (?:good|done|all set)|done|all done|we'?re done)(?:\s+(?:claude|for now))?$/;

/** Is this transcript a request to end the conversation? */
export function isStopPhrase (text) {
  const said = String(text || '').toLowerCase().replace(/[’]/g, "'").replace(/[^a-z' ]+/g, ' ').replace(/\s+/g, ' ').trim();
  return Boolean(said) && STOP.test(said);
}

/** An answer as the phone should say it: no markdown, no bullets, symbols
 *  spelled out. The Lambda is asked for plain speech; this catches the rest. */
export function speakable (text) {
  return String(text || '')
    .replace(/```[\s\S]*?```/g, ' ')
    .replace(/^\s*(?:[-*•]|\d+[.)])\s+/gm, '')
    .replace(/^#+\s*/gm, '')
    .replace(/[*_`#]/g, '')
    .replace(/(\d)\s*°\s*F\b/g, '$1 degrees Fahrenheit')
    .replace(/(\d)\s*°\s*C\b/g, '$1 degrees Celsius')
    .replace(/°/g, ' degrees')
    .replace(/(\d)\s*[–-]\s*(\d)/g, '$1 to $2')
    .replace(/\s*\n+\s*/g, ' ')
    .replace(/\s{2,}/g, ' ')
    .trim();
}

export class VoiceError extends Error {
  constructor (code) {
    const blocked = code === 'not-allowed' || code === 'service-not-allowed';
    super(blocked
      ? 'The microphone is turned off for this app. Allow it in Settings, or type instead.'
      : code === 'audio-capture'
        ? 'No microphone was found.'
        : `Listening stopped (${code}). Tap Talk to start again.`);
    this.code = code;
  }
}

// Safari sometimes never ends a session on its own; after this long with no
// new words, the question is taken as finished.
const QUIET_MS = 1800;

/** Listen for one utterance. `promise` resolves with what was said ('' if
 *  nothing was); `stop()` abandons it. `onWords` sees the words as they
 *  arrive, so the screen can show them; `onListening` fires once the mic is
 *  actually open. */
export function listenOnce ({ onWords, onListening } = {}) {
  const Recognition = recognitionClass();
  const rec = new Recognition();
  rec.lang = 'en-US';
  rec.interimResults = true;
  rec.continuous = false;
  rec.maxAlternatives = 1;
  let heard = '';
  let failure = null;
  let quiet = null;
  let opened = false;
  const markOpen = () => { if (!opened) { opened = true; onListening?.(); } };
  rec.onstart = markOpen;
  rec.onaudiostart = markOpen;
  const promise = new Promise((resolve, reject) => {
    rec.onresult = (event) => {
      heard = Array.from(event.results).map((r) => r[0]?.transcript || '').join('');
      onWords?.(heard);
      clearTimeout(quiet);
      quiet = setTimeout(() => { try { rec.stop(); } catch { /* already ended */ } }, QUIET_MS);
    };
    rec.onerror = (event) => {
      // Silence and our own abort are ordinary ends, not faults.
      if (event.error !== 'no-speech' && event.error !== 'aborted') failure = event.error;
    };
    rec.onend = () => {
      clearTimeout(quiet);
      if (failure) reject(new VoiceError(failure));
      else resolve(heard.trim());
    };
  });
  try {
    rec.start();
  } catch (err) {
    return { promise: Promise.reject(err), stop () {} };
  }
  return { promise, stop: () => { clearTimeout(quiet); try { rec.abort(); } catch { /* already ended */ } } };
}

// Safari (Mac and iPhone) often never fires `end`, and an utterance nothing
// holds on to can be collected mid-sentence and never fire it at all. The old
// stand-in waited a "generous estimate" of the length — about 30 seconds for
// a three-sentence answer — before listening again (bug report, 2026-10-12).
// So the utterance is kept alive here, and the synth itself is asked every
// quarter second whether it is still talking.
//
// That was not enough on the Mac (bug report, 2026-10-27, Safari 27): there
// `speechSynthesis.speaking` can stay true after the voice has stopped, so
// the poll never saw the end and the mic waited out the whole estimate. So
// the words are followed too: Safari fires `boundary` as it reaches each
// one. Reaching the last word, or going quiet once the last sentence has
// begun, is the end. Before the last sentence a gap is only a pause, never
// the end — opening the mic mid-answer would have it hear itself.
const SPEECH_POLL_MS = 250;
// Speech that has not begun by now is never going to (blocked, or nothing
// to say); stop waiting for it.
const SPEECH_START_GRACE_MS = 2000;
// After the last word begins: long enough to say it.
const LAST_WORD_TAIL_MS = 450;
// In the last sentence, this long with no new word means it has finished.
const WORDS_QUIET_MS = 700;
// The backstop if no signal works at all. It is held off while words are
// still arriving, so a slow voice is never cut short by it.
export const backstopMs = (said) => 400 + said.length * 60;
let current = null;

/** Say something. Resolves with `{ how, ms }` once it has been said (or cut
 *  off): `how` is which signal saw the end — 'end' (the event), 'words'
 *  (the last word, or quiet in the last sentence), 'poll' (the synth went
 *  quiet), 'never-started' or 'backstop' — and `ms` how long it took. */
export function speak (text) {
  const synth = globalThis.speechSynthesis;
  const said = speakable(text);
  if (!synth || !said) return Promise.resolve({ how: 'nothing', ms: 0 });
  // Where the last word and the last sentence begin, to recognise them.
  const lastWord = said.search(/\S+\s*$/);
  const sentences = [...said.matchAll(/[.!?]["')\]]*\s+(?=\S)/g)];
  const lastSentence = sentences.length ? sentences[sentences.length - 1].index + sentences[sentences.length - 1][0].length : 0;
  return new Promise((resolve) => {
    let done = false;
    let started = false;
    let lastWordAt = 0;
    let reached = -1;
    let tail = null;
    const begun = Date.now();
    const utterance = new globalThis.SpeechSynthesisUtterance(said);
    const finish = (how) => {
      if (done) return;
      done = true;
      clearTimeout(fallback);
      clearTimeout(tail);
      clearInterval(poll);
      if (current === utterance) current = null;
      resolve({ how, ms: Date.now() - begun });
    };
    utterance.lang = 'en-US';
    utterance.onstart = () => { started = true; };
    utterance.onend = () => finish('end');
    utterance.onerror = () => finish('end');
    utterance.onboundary = (event) => {
      if (event.name && event.name !== 'word') return;
      started = true;
      lastWordAt = Date.now();
      reached = Math.max(reached, event.charIndex ?? -1);
      clearTimeout(tail);
      if (reached >= lastWord) tail = setTimeout(() => finish('words'), LAST_WORD_TAIL_MS);
    };
    let fallback = null;
    const armBackstop = (ms) => {
      fallback = setTimeout(() => {
        // Words still arriving: it is a slow voice, not a stuck one.
        const sinceWord = Date.now() - lastWordAt;
        if (lastWordAt && sinceWord < WORDS_QUIET_MS) armBackstop(WORDS_QUIET_MS - sinceWord);
        else finish('backstop');
      }, ms);
    };
    armBackstop(backstopMs(said));
    const poll = setInterval(() => {
      if (lastWordAt && reached >= lastSentence && Date.now() - lastWordAt >= WORDS_QUIET_MS) return finish('words');
      const busy = synth.speaking || synth.pending;
      if (busy) started = true;
      else if (started) finish('poll');
      else if (Date.now() - begun > SPEECH_START_GRACE_MS) finish('never-started');
    }, SPEECH_POLL_MS);
    current = utterance;
    synth.cancel();
    synth.speak(utterance);
  });
}

/** Cut off anything being said. */
export const stopSpeaking = () => { try { globalThis.speechSynthesis?.cancel(); } catch { /* nothing to stop */ } };

/** iOS only lets speech start from a tap. Saying nothing inside the tap that
 *  starts the conversation unlocks it for the answers that follow. */
export function primeSpeech () {
  const synth = globalThis.speechSynthesis;
  if (!synth || !globalThis.SpeechSynthesisUtterance) return;
  try {
    const silent = new globalThis.SpeechSynthesisUtterance(' ');
    silent.volume = 0;
    synth.speak(silent);
  } catch { /* speech just will not be primed */ }
}
